import { CrmNoteType, CrmTaskStatus } from '@plato/data/prisma';
import { CustomerBusinessStatus } from '@plato/data/starrocks/types';
import { GlobalSearchTab, SearchEntity } from '@plato/search/search-types';
import { useQueryParams } from '@plato/ui-legacy/lib/hooks/use-query-params';
import {
    generateQueryParamsString,
    queryParamsConfig,
    stringArrayParam,
    stringParam,
} from '@plato/ui-legacy/lib/query-params';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ActivityFeedModelType,
    InsightOutcomeResponseFilterOptions,
    OrderStatusFilterOptions,
    QuoteStatusFilterOptions,
} from '../../../server/types/customer-activity-feed-types.ts';
import type { RouterInputs } from '../../../utils/api.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import type { VerticalFilterOption } from '../../filters/index.ts';
import { getNoteTypeLabel } from '../../notes/utils/get-note-type-label.ts';
import { useSearchEntityData } from '../../shared/hooks/use-search-entity.ts';

export type ActivityQueryFilters = Pick<
    RouterInputs['platoUser']['query']['customerActivity']['getFeedList']['withFilters']['filter'],
    | 'searchText'
    | 'orderStatuses'
    | 'quoteStatuses'
    | 'taskStatuses'
    | 'noteTypes'
    | 'insightOutcomeResponses'
    | 'customerStatuses'
    | 'userIds'
    | 'customerIds'
    | 'modelTypes'
> & {
    /** The old feed's single type; the new feed picks several through `modelTypes`. */
    modelType?: ActivityFeedModelType;
};

export type ActivityFilterOption = VerticalFilterOption<ActivityQueryFilters>;

const queryConfig = queryParamsConfig<ActivityQueryFilters>({
    modelType: stringParam<ActivityFeedModelType>(),
    modelTypes: stringArrayParam<NonNullable<ActivityQueryFilters['modelTypes']>>(),
    quoteStatuses: stringArrayParam<NonNullable<ActivityQueryFilters['quoteStatuses']>>(),
    orderStatuses: stringArrayParam<NonNullable<ActivityQueryFilters['orderStatuses']>>(),
    taskStatuses: stringArrayParam<NonNullable<ActivityQueryFilters['taskStatuses']>>(),
    noteTypes: stringArrayParam<NonNullable<ActivityQueryFilters['noteTypes']>>(),
    insightOutcomeResponses: stringArrayParam<NonNullable<ActivityQueryFilters['insightOutcomeResponses']>>(),
    customerStatuses: stringArrayParam<NonNullable<ActivityQueryFilters['customerStatuses']>>(),
    userIds: stringArrayParam<NonNullable<ActivityQueryFilters['userIds']>>(),
    customerIds: stringArrayParam<NonNullable<ActivityQueryFilters['customerIds']>>(),
    searchText: stringParam(),
});

export function useActivityFilters() {
    const defaultFilters: ActivityQueryFilters = {
        orderStatuses: undefined,
        quoteStatuses: undefined,
        taskStatuses: undefined,
        noteTypes: undefined,
        modelType: undefined,
        modelTypes: undefined,
        insightOutcomeResponses: undefined,
        customerStatuses: undefined,
        userIds: undefined,
        customerIds: undefined,
        searchText: '',
    };

    const [appliedFilters, setAppliedFilters] = useQueryParams(queryConfig, defaultFilters);

    return {
        pageSize: 10,
        defaultFilters,
        appliedFilters,
        setAppliedFilters,
    };
}

export type ActivityFeedLinkSource = 'team' | 'direct';

export function generateQueryParamsStringForActivity(
    filters: Partial<ActivityQueryFilters>,
    options?: { source?: ActivityFeedLinkSource },
) {
    return generateQueryParamsString(
        queryConfig,
        filters,
        options?.source ? new URLSearchParams({ source: options.source }) : undefined,
    );
}

/** A type's status filter only makes sense while that type can still appear in the feed. */
function isTypeShown(filters: ActivityQueryFilters, type: ActivityFeedModelType): boolean {
    if (filters.modelTypes?.length) {
        return filters.modelTypes.includes(type);
    }

    return !filters.modelType || filters.modelType === type;
}

export function useQuoteStatusFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const getQuoteStatusLabel = (status: QuoteStatusFilterOptions): string => {
        switch (status) {
            case QuoteStatusFilterOptions.OPEN:
                return t('OPEN', { ns: 'order-status' });
            case QuoteStatusFilterOptions.PARTIALLY_RECEIVED:
                return t('PARTIALLY_RECEIVED', { ns: 'order-status' });
            case QuoteStatusFilterOptions.RECEIVED:
                return t('RECEIVED', { ns: 'order-status' });
            case QuoteStatusFilterOptions.LOST:
                return t('LOST', { ns: 'order-status' });
            case QuoteStatusFilterOptions.REVISED:
                return t('REVISED', { ns: 'order-status' });
            case QuoteStatusFilterOptions.OTHER:
                return t('OTHER', { ns: 'order-status' });
        }
    };
    const { appliedFilters } = useActivityFilters();

    if (!isTypeShown(appliedFilters, ActivityFeedModelType.QUOTE)) {
        return undefined;
    }

    return {
        id: 'quoteStatuses',
        label: t('customer_detail__quotes'),
        type: 'checkbox' as const,
        options: Object.values(QuoteStatusFilterOptions).map((status) => ({
            id: status,
            value: status,
            label: getQuoteStatusLabel(status),
        })),
    };
}

export function useOrderStatusFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const getOrderStatusLabel = (status: OrderStatusFilterOptions): string => {
        switch (status) {
            case OrderStatusFilterOptions.OPEN:
                return t('OPEN', { ns: 'order-status' });
            case OrderStatusFilterOptions.PARTIALLY_BILLED:
                return t('PARTIALLY_BILLED', { ns: 'order-status' });
            case OrderStatusFilterOptions.BILLED:
                return t('BILLED', { ns: 'order-status' });
            case OrderStatusFilterOptions.PROCESSED_INTERNALLY:
                return t('PROCESSED_INTERNALLY', { ns: 'order-status' });
            case OrderStatusFilterOptions.OPEN_IN_PICKING:
                return t('OPEN_IN_PICKING', { ns: 'order-status' });
            case OrderStatusFilterOptions.PARTIALLY_PICKED:
                return t('PARTIALLY_PICKED', { ns: 'order-status' });
            case OrderStatusFilterOptions.PICKED:
                return t('PICKED', { ns: 'order-status' });
            case OrderStatusFilterOptions.PARTIALLY_SHIPPED:
                return t('PARTIALLY_SHIPPED', { ns: 'order-status' });
            case OrderStatusFilterOptions.RELEASED_FOR_DELIVERY:
                return t('RELEASED_FOR_DELIVERY', { ns: 'order-status' });
            case OrderStatusFilterOptions.SHIPPED:
                return t('SHIPPED', { ns: 'order-status' });
            case OrderStatusFilterOptions.RETURNED:
                return t('RETURNED', { ns: 'order-status' });
            case OrderStatusFilterOptions.CANCELLED:
                return t('CANCELLED', { ns: 'order-status' });
        }
    };
    const { appliedFilters } = useActivityFilters();

    if (!isTypeShown(appliedFilters, ActivityFeedModelType.ORDER)) {
        return undefined;
    }

    return {
        id: 'orderStatuses',
        label: t('customer_detail__orders'),
        type: 'checkbox' as const,
        options: Object.values(OrderStatusFilterOptions).map((status) => ({
            id: status,
            value: status,
            label: getOrderStatusLabel(status),
        })),
    };
}

export function useTaskStatusFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const { appliedFilters } = useActivityFilters();

    if (!isTypeShown(appliedFilters, ActivityFeedModelType.TASK)) {
        return undefined;
    }

    return {
        id: 'taskStatuses',
        label: t('customer_detail__tasks'),
        type: 'checkbox' as const,
        options: [
            { label: t('customer_detail__open_tasks'), id: CrmTaskStatus.OPEN, value: CrmTaskStatus.OPEN },
            {
                label: t('customer_detail__completed_tasks'),
                id: CrmTaskStatus.COMPLETED,
                value: CrmTaskStatus.COMPLETED,
            },
        ],
    };
}

export function useNoteTypeFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const { appliedFilters } = useActivityFilters();

    if (!isTypeShown(appliedFilters, ActivityFeedModelType.NOTE)) {
        return undefined;
    }

    return {
        id: 'noteTypes',
        label: t('customer_detail__notes'),
        type: 'checkbox' as const,
        options: [
            {
                label: getNoteTypeLabel(CrmNoteType.VISIT, t),
                id: CrmNoteType.VISIT,
                value: CrmNoteType.VISIT,
            },
            {
                label: getNoteTypeLabel(CrmNoteType.PHONE_CALL, t),
                id: CrmNoteType.PHONE_CALL,
                value: CrmNoteType.PHONE_CALL,
            },
            { label: getNoteTypeLabel(CrmNoteType.NOTE, t), id: CrmNoteType.NOTE, value: CrmNoteType.NOTE },
        ],
    };
}

export function useInsightOutcomeFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const { appliedFilters } = useActivityFilters();

    if (!isTypeShown(appliedFilters, ActivityFeedModelType.INSIGHT_OUTCOME)) {
        return undefined;
    }

    return {
        id: 'insightOutcomeResponses',
        label: t('customer_detail__insight_outcomes'),
        type: 'checkbox' as const,
        options: [
            {
                id: InsightOutcomeResponseFilterOptions.INTERESTED,
                value: InsightOutcomeResponseFilterOptions.INTERESTED,
                label: t('insight_outcome__response_INTERESTED', { ns: 'insights' }),
            },
            {
                id: InsightOutcomeResponseFilterOptions.NO_NEED,
                value: InsightOutcomeResponseFilterOptions.NO_NEED,
                label: t('insight_outcome__response_NO_NEED', { ns: 'insights' }),
            },
            {
                id: InsightOutcomeResponseFilterOptions.BUYS_ELSEWHERE,
                value: InsightOutcomeResponseFilterOptions.BUYS_ELSEWHERE,
                label: t('insight_outcome__response_BUYS_ELSEWHERE', { ns: 'insights' }),
            },
        ],
    };
}

export function useCustomerStatusFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const { appliedFilters } = useActivityFilters();

    if (!isTypeShown(appliedFilters, ActivityFeedModelType.CUSTOMER_STATUS_CHANGE)) {
        return undefined;
    }

    return {
        id: 'customerStatuses',
        label: t('customer_detail__status_changes'),
        type: 'checkbox' as const,
        options: [
            {
                id: CustomerBusinessStatus.INACTIVE,
                value: CustomerBusinessStatus.INACTIVE,
                label: t('customer_detail__marked_inactive'),
            },
            {
                id: CustomerBusinessStatus.ACTIVE,
                value: CustomerBusinessStatus.ACTIVE,
                label: t('customer_detail__marked_active'),
            },
        ],
    };
}

export function useUserFilter(): ActivityFilterOption | undefined {
    const { t } = useTranslation('customer-detail');
    const { data: users } = api.platoUser.query.users.getManyUsers.getSubordinates.useQuery();

    if (!users || users.length <= 1) {
        return undefined;
    }

    return {
        id: 'userIds',
        label: t('activity_feed__filter_users'),
        type: 'checkbox' as const,
        options: users.map((user) => ({
            id: user.id,
            value: user.id,
            label: getFullName(user.firstName, user.lastName),
        })),
    };
}

// `selectedCustomerIds` is the live selection (applied ∪ in-modal staged), so names resolve
// for customers picked during this session before Apply — not only applied/searched ones.
export function useCustomerFilter(selectedCustomerIds: string[]): ActivityFilterOption {
    const { t } = useTranslation('customer-detail');
    const [search, setSearch] = useState('');

    const { data, isLoading, trackResultClicked } = useSearchEntityData({
        entities: [SearchEntity.Customer],
        search,
        selectedTabKey: GlobalSearchTab.Customers,
        tracking: { context: 'activity_feed_customer_filter' },
    });

    const searchResults = useMemo(() => data.get(SearchEntity.Customer) ?? [], [data]);

    const { data: selectedCustomers } = api.platoUser.getManyCustomers.getByIds.useQuery(
        { customerIds: selectedCustomerIds },
        { enabled: selectedCustomerIds.length > 0, placeholderData: keepPreviousData },
    );

    const options = useMemo(() => {
        const nameById = new Map<string, string>();
        for (const customer of selectedCustomers ?? []) {
            nameById.set(customer.customerId, customer.customerName ?? customer.customerId);
        }

        for (const customer of searchResults) {
            nameById.set(customer.id, customer.name);
        }

        const selectedOptions = selectedCustomerIds.map((id) => ({
            id,
            value: id,
            label: nameById.get(id) ?? id,
        }));
        const searchOptions = searchResults
            .filter((customer) => !selectedCustomerIds.includes(customer.id))
            .map((customer) => ({ id: customer.id, value: customer.id, label: customer.name }));

        return [...selectedOptions, ...searchOptions];
    }, [selectedCustomers, selectedCustomerIds, searchResults]);

    return {
        id: 'customerIds',
        label: t('activity_feed__filter_customers'),
        type: 'checkbox' as const,
        options,
        onSearchOptions: setSearch,
        onSelectOption: (id) => {
            const clicked = searchResults.find((customer) => customer.id === id);
            if (clicked) {
                trackResultClicked(clicked);
            }
        },
        isLoadingOptions: isLoading,
    };
}
